"use client";

/**
 * A leader's profile, opened from their photo or name in an event's details: a
 * larger photo, the name and what they wrote about themselves, which is plain
 * text, so it keeps its line breaks. A sheet rising from the bottom on a phone
 * and a centred window on a desktop, as the other sheets are.
 *
 * Loaded when it opens, since the events carry only the name and a thumbnail.
 * openLeader(id) works from anywhere; LeaderHost is mounted once by the page.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { cdnUrl } from "@/lib/cdn";
import type { LeaderProfile } from "@/lib/types/event";
import { Sheet } from "@/components/ui/Sheet";
import { Loading } from "@/components/ui/Spinner";

let opener: ((leaderId: number | string) => void) | null = null;

export const openLeader = (leaderId: number | string) => opener?.(leaderId);

export function LeaderHost() {
  const [leaderId, setLeaderId] = useState<number | string | null>(null);

  useEffect(() => {
    opener = (id) => setLeaderId(id);
    return () => {
      opener = null;
    };
  }, []);

  return (
    <Sheet open={leaderId !== null} onClose={() => setLeaderId(null)} title="" width={420}>
      {leaderId !== null && <LeaderPopup leaderId={leaderId} />}
    </Sheet>
  );
}

export function LeaderPopup({ leaderId }: { leaderId: number | string }) {
  const [leader, setLeader] = useState<LeaderProfile | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    api.get<LeaderProfile>(`/public-api/leader/${leaderId}`).then((answer) => {
      if (!cancelled) {
        setLeader(answer);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [leaderId]);

  if (leader === undefined) {
    return <Loading className="py-12" />;
  }
  if (!leader) {
    return <p className="px-6 pb-8 text-center text-ink-soft">Não foi possível carregar o perfil.</p>;
  }
  const description = String(leader.description || "").trim();
  return (
    <div className="flex flex-col items-center px-6 pb-8 pt-2 text-center">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={cdnUrl(leader.photo || leader.photo_thumbnail || "user.png")}
        alt=""
        className="h-36 w-36 rounded-full object-cover ring-4 ring-brand-soft"
        width={144}
        height={144}
      />
      <h2 className="mt-4 text-[20px] font-extrabold">{leader.name}</h2>
      <p className="mt-0.5 text-[12.5px] font-semibold uppercase tracking-wide text-brand">Líder</p>
      {description && <p className="mt-4 whitespace-pre-line text-left text-[14.5px] leading-relaxed text-ink-soft">{description}</p>}
    </div>
  );
}
